import React, { useState } from 'react';
import { Alert, Badge, Col, Form, Row, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import {
  useCandidateCommunicationQuery,
  useSendCandidateEmailMutation,
} from '../../../../__generated__/graphql';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import { useToasts } from '../../../../../../context/Toast';
import Button from '../../../../../../components/button';
import StringToHtml from '../../../../../../components/StringToHtml';
import ModalSidebar, {
  ModalSidebarContent,
} from '../../../../components/ModalSidebar';
import Section from '../Section';
import {
  Message,
  formatSender,
  sortByDateAscending,
  toHtmlBody,
  toReplySubject,
} from './logic';
import './Communication.css';

type Props = {
  candidateId: string;
  candidateEmail: string | null;
};

type ComposerState = {
  toAddress: string;
  ccAddress: string;
  bccAddress: string;
  subject: string | null;
  body: string;
  senderId: string;
};

const i18Path = 'recruitment.candidate.communication';

const Communication: React.FC<Props> = ({ candidateId, candidateEmail }) => {
  const t = useNamespacedTranslation(i18Path);
  const { addSuccess, addError } = useToasts();

  const { data, loading, error, refetch } = useCandidateCommunicationQuery({
    variables: { candidateId },
  });
  const [sendCandidateEmail, { loading: sending }] =
    useSendCandidateEmailMutation();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [composer, setComposer] = useState<ComposerState>({
    toAddress: candidateEmail ?? '',
    ccAddress: '',
    bccAddress: '',
    subject: null,
    body: '',
    senderId: '',
  });
  const [showCcBcc, setShowCcBcc] = useState<boolean>(false);

  const messages = data
    ? sortByDateAscending(data.candidateCommunication.messages)
    : [];
  const senders = data?.candidateCommunication.senders ?? [];
  // Sending requires a verified org-wide email address so candidate replies
  // route back into Salesforce; without one the composer would send from the
  // wrong mailbox and replies would never appear in the thread.
  const canSend = senders.length > 0;
  const lastMessage =
    messages.length > 0 ? messages[messages.length - 1] : null;
  const effectiveSenderId = composer.senderId || senders[0]?.id || '';
  const effectiveSubject = composer.subject ?? toReplySubject(lastMessage);

  const setField = (field: keyof ComposerState, value: string) =>
    setComposer((prev) => ({ ...prev, [field]: value }));

  const handleSend = async () => {
    if (
      !composer.toAddress.trim() ||
      !effectiveSubject.trim() ||
      !composer.body.trim()
    ) {
      addError(t('validationError'));
      return;
    }
    // Stay busy through the refetch so a second send can't thread against the
    // stale lastMessage before the just-sent message lands in the thread.
    setBusy(true);
    try {
      await sendCandidateEmail({
        variables: {
          input: {
            candidateId,
            toAddress: composer.toAddress,
            ccAddress: composer.ccAddress || null,
            bccAddress: composer.bccAddress || null,
            subject: effectiveSubject,
            htmlBody: toHtmlBody(composer.body),
            parentEmailMessageId: lastMessage?.id ?? null,
            orgWideEmailAddressId: effectiveSenderId || null,
          },
        },
      });
    } catch {
      addError(t('sendError'));
      setBusy(false);
      return;
    }
    setComposer((prev) => ({ ...prev, subject: null, body: '' }));
    addSuccess(t('sendSuccess'));
    // The email is already sent; a failed refetch must not surface sendError.
    await refetch().catch(() => undefined);
    setBusy(false);
  };

  const composerFields = (
    <>
      <h4>{lastMessage ? t('composer.replyTitle') : t('composer.title')}</h4>
      <Form.Group className="mb-2">
        <Form.Label>{t('composer.to')}</Form.Label>
        <Form.Control
          type="email"
          value={composer.toAddress}
          onChange={(e) => setField('toAddress', e.target.value)}
        />
      </Form.Group>
      {!showCcBcc && (
        <div className="mb-2">
          <Button
            variant="link"
            size="sm"
            label={t('composer.showCcBcc')}
            onClick={() => setShowCcBcc(true)}
          />
        </div>
      )}
      {showCcBcc && (
        <Row>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label>{t('composer.cc')}</Form.Label>
              <Form.Control
                value={composer.ccAddress}
                onChange={(e) => setField('ccAddress', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-2">
              <Form.Label>{t('composer.bcc')}</Form.Label>
              <Form.Control
                value={composer.bccAddress}
                onChange={(e) => setField('bccAddress', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      )}
      <Form.Group className="mb-2">
        <Form.Label>{t('composer.from')}</Form.Label>
        <Form.Select
          value={effectiveSenderId}
          onChange={(e) => setField('senderId', e.target.value)}>
          {senders.map((sender) => (
            <option key={sender.id} value={sender.id}>
              {formatSender(sender)}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>{t('composer.subject')}</Form.Label>
        <Form.Control
          value={effectiveSubject}
          onChange={(e) => setField('subject', e.target.value)}
        />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>{t('composer.message')}</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          value={composer.body}
          onChange={(e) => setField('body', e.target.value)}
        />
      </Form.Group>
      <Button
        variant="primary"
        label={t('composer.send')}
        disabled={busy || sending}
        isProcessing={busy || sending}
        onClick={handleSend}
      />
    </>
  );

  const panelBody = (
    <>
      {messages.length === 0 ? (
        <p className="text-muted">{t('noMessages')}</p>
      ) : (
        <div className="d-flex flex-column gap-3">
          {messages.map((message) => (
            <MessageItem key={message.id} message={message} />
          ))}
        </div>
      )}
      <hr />
      {canSend ? (
        composerFields
      ) : (
        <Alert variant="info" className="mb-0">
          <div className="fw-bold mb-1">{t('noSender.title')}</div>
          <div>{t('noSender.description')}</div>
        </Alert>
      )}
    </>
  );

  return (
    <Section title={t('title')}>
      <div className="d-flex justify-content-between align-items-center">
        <span className="text-muted">
          {loading ? (
            <Spinner animation="border" size="sm" />
          ) : error ? (
            t('loadError')
          ) : (
            t('messageSummary', { count: messages.length })
          )}
        </span>
        <Button
          variant="outline-primary"
          label={t('open')}
          disabled={loading || !!error}
          onClick={() => setOpen(true)}
        />
      </div>
      {open && data && (
        <ModalSidebar
          show={open}
          onClose={() => setOpen(false)}
          header={t('title')}
          size="lg"
          content={<ModalSidebarContent body={panelBody} />}
        />
      )}
    </Section>
  );
};

const MessageItem: React.FC<{ message: Message }> = ({ message }) => {
  const t = useNamespacedTranslation(i18Path);

  return (
    <div className="border rounded p-3">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div>
          <Badge bg={message.incoming ? 'info' : 'primary'} className="me-2">
            {message.incoming ? t('message.received') : t('message.sent')}
          </Badge>
          <span className="fw-bold">
            {message.fromName ?? message.fromAddress}
          </span>
          {message.fromName && message.fromAddress && (
            <span className="text-muted ms-1">
              {'<' + message.fromAddress + '>'}
            </span>
          )}
        </div>
        {message.messageDate && (
          <span className="text-muted small">
            {format(new Date(message.messageDate), 'PPp')}
          </span>
        )}
      </div>
      {message.subject && <div className="fw-bold mb-1">{message.subject}</div>}
      <div className="candidate-communication-body">
        {message.htmlBody ? (
          <StringToHtml text={message.htmlBody} />
        ) : (
          <span className="candidate-communication-text">
            {message.textBody}
          </span>
        )}
      </div>
    </div>
  );
};

export default Communication;
